/* eslint-disable @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unsafe-assignment */
import { createRouter, createWebHistory, type RouteLocationNormalizedGeneric, type RouteRecordRaw } from 'vue-router';
import AppLayout from '@/layout/AppLayout.vue';
import posthog from '@/composables/usePostHog.js';
import { isReadOnlyImpersonation } from '@/util/apiClient';
import { stores } from '@/util/stores.js';
import { publicRoutes } from '@/router/routes/public.routes.js';
import { employeeRoutes } from '@/router/routes/employee.routes.js';
import { refAdminRoutes } from '@/router/routes/refadmin.routes.js';
import { assignorRoutes } from '@/router/routes/assignor.routes.js';
import { officialRoutes } from '@/router/routes/official.routes.js';
import { userRoutes } from '@/router/routes/user.routes.js';
import { supportRoutes } from '@/router/routes/support.routes.js';
import { clientRoutes } from '@/router/routes/client.routes.js';
import { appMiscRoutes } from '@/router/routes/appMisc.routes.js';
import { sharedRoutes } from '@/router/routes/shared.routes';
import logger from '@/util/logging/logger.js';
import { getDefaultRouteNameForRoles } from '@/router/defaultRoleDestination';

const IMPERSONATION_BLOCKED_ROUTES = ['profile', 'User Settings', 'Officials Financial Records', 'Assignors Financial Records'] as const;

const isPublic = (to: RouteLocationNormalizedGeneric) => to.matched.some((record) => record.meta?.['public'] === true);
const requiresAuth = (to: RouteLocationNormalizedGeneric) => to.matched.some((record) => record.meta?.['auth'] === true);

const requiredAnyRoles = (to: RouteLocationNormalizedGeneric): string[] | null => {
	const record = [...to.matched].reverse().find((matchedRecord) => Array.isArray(matchedRecord.meta['anyOf']));
	return Array.isArray(record?.meta['anyOf']) ? (record.meta['anyOf'] as string[]) : null;
};

const hasAnyRole = (userRoles: string[], anyOfRoles: string[] | null) => !anyOfRoles || anyOfRoles.some((role) => userRoles.includes(role));

const isImpersonationBlockedRoute = (routeName: RouteLocationNormalizedGeneric['name']) => typeof routeName === 'string' && IMPERSONATION_BLOCKED_ROUTES.includes(routeName as (typeof IMPERSONATION_BLOCKED_ROUTES)[number]);

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		{
			path: '/',
			children: publicRoutes
		},
		{
			path: '/app',
			component: AppLayout,
			meta: { auth: true },
			children: [...employeeRoutes, ...refAdminRoutes, ...assignorRoutes, ...officialRoutes, ...userRoutes, ...clientRoutes, ...supportRoutes, ...appMiscRoutes, ...sharedRoutes] as RouteRecordRaw[]
		}
	] as RouteRecordRaw[]
});

router.beforeEach(async (to, from) => {
	if (isPublic(to)) {
		return true;
	}

	await stores.authStore.ensureInitialized();

	const isAuthenticated = stores.authStore.isAuthenticatedSession();
	if (requiresAuth(to) && !isAuthenticated) {
		// A deep link (an email's "finish your setup", a shared page) signs in first and then comes back here.
		if (to.path === '/app' || to.path === '/app/') {
			return { name: 'landing' };
		}
		return { name: 'login', query: { returnTo: to.fullPath } };
	}

	if (isAuthenticated) {
		stores.usersStore.hydrateGroupsFromStorage();
		if (stores.usersStore.roles.length === 0) {
			try {
				await stores.usersStore.usersGroups();
			} catch (error) {
				logger.warn('Failed to hydrate group roles during navigation', { error });
			}
		}
	}

	const userRoles = stores.usersStore.roles;

	if (isImpersonationBlockedRoute(to.name) && isReadOnlyImpersonation()) {
		posthog.capture('impersonation_route_blocked', { routeName: to.name });

		if (from.name && !isImpersonationBlockedRoute(from.name)) {
			return false;
		}

		return { name: getDefaultRouteNameForRoles(userRoles) };
	}

	const anyOfRoles = requiredAnyRoles(to);
	if (isAuthenticated && userRoles.length === 0) {
		return true;
	}
	if (!hasAnyRole(userRoles, anyOfRoles)) {
		return { name: 'Private Access Denied' };
	}

	return true;
});

router.afterEach((to) => {
	try {
		posthog.capture('$pageview', {
			path: to.fullPath,
			name: to.name
		});
	} catch {}
});

export default router;
